import { CommonModule } from '@angular/common';
import { MaskService } from '../../../../../shared/services/mask.service';
import { UtilService } from '../../../../../shared/services/util.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepicker } from '@angular/material/datepicker';
import { TipoParcelamento } from '../../../../../shared/services/models/financas/tipo-parcelamento.model';
import { FrequenciaRecorrencia } from '../../../../../shared/services/models/financas/frequencia-recorrencia.model';
import { ContaFinanceira } from '../../../../../shared/services/models/financas/conta-financeira.model';
import { FormaPagamento } from '../../../../../shared/services/models/financas/forma-pagamento.model';
import { ContasPagarService } from '../../../../../shared/services/apis/financas/contas-pagar.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { CampoObrigatorioStruct } from '../../../../../shared/services/structs/financas/campo-obrigatorio.struct';
import { TipoContaEnum } from '../../../../../shared/enums/financas/tipo-conta.enum';
import { FrequenciaRecorrenciaEnum } from '../../../../../shared/enums/financas/frequencia-recorrencia.enum';
import { ContasPagarUtil } from '@shared/helpers/contas-pagar.util';
import { normalizarValorOrdenacao } from '../../../../../shared/helpers/ordenacao-tabela.helper';

@Component({
  selector: 'app-contas-pagar-parcelamento',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    SelectPesquisavelComponent,
    AlertCardComponent,
    AbntPipe
  ],
  templateUrl: './contas-pagar-parcelamento.component.html',
  styleUrl: './contas-pagar-parcelamento.component.css'
})
export class ContasPagarParcelamentoComponent implements OnInit {

  @Input() parentForm!: FormGroup;
  @Input() isEditMode: boolean = false;
  @Input() qtdParcelas: number = 0;
  @Input() listaTiposParcelamento: TipoParcelamento[] = [];
  @Input() listaFrequencia: FrequenciaRecorrencia[] = [];
  @Input() listaContaFinanceira: ContaFinanceira[] = [];
  @Input() listaFormaPagamento: FormaPagamento[] = [];
  @Input() currencyMask: any;
  @Input() integerMask: any;
  @Input() hasParcelasEmAberto: boolean = false;
  @Input() idContasPagar: number | null = null;
  @Input() camposObrigatorios: CampoObrigatorioStruct[] = [];

  @Output() renegociarClick = new EventEmitter<void>();

  get isMultiParcela(): boolean {
    return this.isEditMode && this.qtdParcelas > 1;
  }

  public isSimulando: boolean = false;

  get parcelamentoGroup(): FormGroup {
    return this.parentForm.get('parcelamento') as FormGroup;
  }

  get isRecorrente(): boolean {
    return this.parcelamentoGroup.get('isRecorrente')?.value;
  }

  /**
   * A lista de frequências vem de um lookup no banco (tabela frequencia_recorrencia) e hoje tem linhas
   * extras (Bimestral/Trimestral/Semestral) que não existem no enum e são calculadas erradas (como se
   * fossem 1 mês) — decisão do Pedro foi remover essas opções da tela. Como a remoção das linhas em si
   * é uma ação de banco (fora do escopo do código), filtramos aqui pelos ids que o enum realmente suporta
   * (Dia/Semana/Mês/Ano) até a limpeza ser feita no banco.
   */
  get listaFrequenciaFiltrada(): FrequenciaRecorrencia[] {
    const idsSuportados = new Set(Object.values(FrequenciaRecorrenciaEnum).filter(v => typeof v === 'number'));
    return this.listaFrequencia.filter(f => idsSuportados.has(f.idFrequenciaRecorrencia));
  }

  /**
   * "Dia de Vencimento Fixo" só se aplica a recorrência mensal/anual. Em diária/semanal o
   * vencimento avança naturalmente, então o campo é ocultado.
   */
  get mostrarDiaVencimentoFixo(): boolean {
    const freq = this.parcelamentoGroup.get('idFrequenciaRecorrencia')?.value;
    return freq === FrequenciaRecorrenciaEnum.Mes || freq === FrequenciaRecorrenciaEnum.Ano;
  }

  get diaVencimentoMask(): any {
    return { ...this.integerMask, max: 31 };
  }

  get parcelasArray(): FormArray {
    return this.parcelamentoGroup.get('parcelas') as FormArray;
  }

  get idFormaPagamentoControl(): any { return this.parcelamentoGroup.get('idFormaPagamento'); }
  get idContaFinanceiraControl(): any { return this.parcelamentoGroup.get('idContaFinanceira'); }

  get contaSelecionada(): ContaFinanceira | null {
    const id = this.idContaFinanceiraControl?.value;
    if (!id) return null;
    return this.listaContaFinanceira.find(c => c.idContaFinanceira === id) ?? null;
  }

  get isContaCartao(): boolean {
    return this.contaSelecionada?.idTipoConta === TipoContaEnum.CartaoCredito;
  }

  get diaFechamentoCartao(): number | null {
    return this.contaSelecionada?.diaFechamentoCartao ?? null;
  }

  get diaVencimentoCartao(): number | null {
    return this.contaSelecionada?.diaVencimentoCartao ?? null;
  }

  /**
   * Forma de pagamento não é um enum fixo (lookup livre por empresa), então a
   * busca por "Cartão de crédito" é por texto — cuidado para não casar com "Cartão de débito".
   */
  private get formaPagamentoCartaoCredito(): FormaPagamento | null {
    return this.listaFormaPagamento.find(f => {
      const desc = normalizarValorOrdenacao(f.descricao) as string;
      return desc.includes('cartao') && desc.includes('credito');
    }) ?? null;
  }

  /**
   * Filtro do datepicker para conta cartão: permite apenas datas a partir
   * do primeiro dia do mês da fatura destino.
   */
  public cartaoDateFilter = (d: Date | null): boolean => {
    if (!d) return false;
    if (!this.isContaCartao) return true;
    const fechamento = this.diaFechamentoCartao;
    const vencimento = this.diaVencimentoCartao;
    if (fechamento == null || vencimento == null) return true;
    const mesMinimo = ContasPagarUtil.calcularMesFaturaDestino(new Date(), fechamento, vencimento);
    return new Date(d.getFullYear(), d.getMonth(), 1) >= mesMinimo;
  };

  get tipoParcelamentoSelecionado(): TipoParcelamento | null {
    const id = this.parcelamentoGroup.get('idTipoParcelamento')?.value;
    if (!id) return null;
    return this.listaTiposParcelamento.find(t => t.idTipoParcelamento === id) ?? null;
  }

  get isParcelado(): boolean {
    const tipo = this.tipoParcelamentoSelecionado;
    return tipo != null && tipo.quantidadeParcelas > 1;
  }

  get podeSimular(): boolean {
    return !this.isSimulando;
  }

  public isObrigatorio(nomeTecnico: string): boolean {
    return this.camposObrigatorios.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);
  }

  constructor(
    private fb: FormBuilder,
    private contasPagarService: ContasPagarService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.parcelamentoGroup.get('isRecorrente')?.valueChanges.subscribe(() => {
      this.parcelasArray.clear();
      this.parcelamentoGroup.patchValue({
        idTipoParcelamento: null,
        dataVencimento: null,
        intervaloDiasParcelas: null,
        recorrenciaIntervalo: null,
        idFrequenciaRecorrencia: null,
        recorrenciaDiaVencimento: null,
        recorrenciaQuantidade: null,
        isPago: false
      }, { emitEvent: false });
      this.aplicarRegrasContaCartao();
    });

    // Em frequência diária/semanal o "dia de vencimento fixo" não se aplica — limpa o valor.
    this.parcelamentoGroup.get('idFrequenciaRecorrencia')?.valueChanges.subscribe(() => {
      if (!this.mostrarDiaVencimentoFixo) {
        this.parcelamentoGroup.get('recorrenciaDiaVencimento')?.setValue(null, { emitEvent: false });
      }
    });

    // Reagir à troca de conta financeira (aplica regras quando vira cartão e
    // reverte quando deixa de ser).
    this.idContaFinanceiraControl?.valueChanges.subscribe(() => {
      this.aplicarRegrasContaCartao();
    });

    // Ao salvar a data de vencimento em conta cartão, ajustar o dia para o
    // dia de vencimento da fatura — independente do que o usuário escolher.
    this.parcelamentoGroup.get('dataVencimento')?.valueChanges.subscribe((data) => {
      if (!this.isContaCartao || !data) return;
      const dia = this.diaVencimentoCartao;
      if (dia == null) return;
      const dataAjustada = ContasPagarUtil.ajustarParaDiaVencimento(new Date(data), dia);
      if (new Date(data).getTime() !== dataAjustada.getTime()) {
        this.parcelamentoGroup.get('dataVencimento')?.setValue(dataAjustada, { emitEvent: false });
      }
    });
  }

  /**
   * Quando a conta financeira selecionada é cartão de crédito:
   * - trava frequência de recorrência em Mensal
   * - trava dia de vencimento fixo no dia do cartão
   * - desabilita intervalo de dias entre parcelas (não se aplica)
   * - trava forma de pagamento em "Cartão de crédito" (conta cartão só paga por cartão)
   * Quando deixa de ser cartão, reverte os disables.
   */
  private aplicarRegrasContaCartao(): void {
    const intervaloDias = this.parcelamentoGroup.get('intervaloDiasParcelas');
    const freq = this.parcelamentoGroup.get('idFrequenciaRecorrencia');
    const diaFixo = this.parcelamentoGroup.get('recorrenciaDiaVencimento');
    const formaPagamento = this.idFormaPagamentoControl;

    if (this.isContaCartao && this.diaVencimentoCartao != null) {
      intervaloDias?.setValue(null, { emitEvent: false });
      intervaloDias?.disable({ emitEvent: false });

      freq?.setValue(FrequenciaRecorrenciaEnum.Mes, { emitEvent: false });
      freq?.disable({ emitEvent: false });

      diaFixo?.setValue(this.diaVencimentoCartao, { emitEvent: false });
      diaFixo?.disable({ emitEvent: false });

      const formaCartao = this.formaPagamentoCartaoCredito;
      if (formaCartao) {
        formaPagamento?.setValue(formaCartao.idFormaPagamento, { emitEvent: false });
        formaPagamento?.disable({ emitEvent: false });
      }

      const mesDestino = ContasPagarUtil.calcularMesFaturaDestino(
        new Date(), this.diaFechamentoCartao!, this.diaVencimentoCartao!
      );
      const dataCorreta = ContasPagarUtil.ajustarParaDiaVencimento(mesDestino, this.diaVencimentoCartao!);
      this.parcelamentoGroup.get('dataVencimento')?.setValue(dataCorreta, { emitEvent: false });
    } else {
      intervaloDias?.enable({ emitEvent: false });
      freq?.enable({ emitEvent: false });
      diaFixo?.enable({ emitEvent: false });
      formaPagamento?.enable({ emitEvent: false });
    }
  }

  public simular(): void {
    if (this.isRecorrente) {
      this.simularRecorrencia();
    } else {
      this.simularParcelas();
    }
  }

  private simularParcelas(): void {
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    const idTipoParcelamento = this.parcelamentoGroup.get('idTipoParcelamento')?.value;
    const dataVencimento = this.parcelamentoGroup.get('dataVencimento')?.value;
    const intervaloDiasParcelas = this.parcelamentoGroup.get('intervaloDiasParcelas')?.value;
    const descricaoBase = this.parentForm.get('dadosGerais.descricao')?.value || '';

    if (!valorTotal || valorTotal <= 0) {
      this.alertService.show('Atenção', 'Preencha o valor total do documento em Dados Gerais.', AlertType.warning);
      return;
    }
    if (!idTipoParcelamento) {
      this.alertService.show('Atenção', 'Selecione o tipo de parcelamento.', AlertType.warning);
      return;
    }
    if (!dataVencimento) {
      this.alertService.show('Atenção', 'Informe a data de vencimento inicial.', AlertType.warning);
      return;
    }

    const request = {
      valorTotal,
      idTipoParcelamento,
      dataVencimento: new Date(dataVencimento).toISOString(),
      intervaloDiasParcelas: intervaloDiasParcelas || 30,
      descricaoBase,
      idContaFinanceira: this.idContaFinanceiraControl?.value ?? null
    };

    this.isSimulando = true;
    this.contasPagarService.simularParcelas(request).subscribe({
      next: (res: any) => {
        this.isSimulando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Erro ao simular parcelas.', AlertType.error);
          return;
        }
        this.popularGridParcelas(res.parcelas || []);
      },
      error: () => {
        this.isSimulando = false;
        this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
      }
    });
  }

  private simularRecorrencia(): void {
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    const intervalo = this.parcelamentoGroup.get('recorrenciaIntervalo')?.value || 1;
    const idFrequenciaRecorrencia = this.parcelamentoGroup.get('idFrequenciaRecorrencia')?.value;
    const diaVencimentoFixo = this.parcelamentoGroup.get('recorrenciaDiaVencimento')?.value || 0;
    const quantidadeVezes = this.parcelamentoGroup.get('recorrenciaQuantidade')?.value;
    const descricaoBase = this.parentForm.get('dadosGerais.descricao')?.value || '';
    const dataInicio = this.parcelamentoGroup.get('dataVencimento')?.value;

    if (!valorTotal || valorTotal <= 0) {
      this.alertService.show('Atenção', 'Preencha o valor total do documento em Dados Gerais.', AlertType.warning);
      return;
    }
    if (!quantidadeVezes || quantidadeVezes < 1) {
      this.alertService.show('Atenção', 'Informe a quantidade de vezes.', AlertType.warning);
      return;
    }
    if (!idFrequenciaRecorrencia) {
      this.alertService.show('Atenção', 'Selecione o período da recorrência.', AlertType.warning);
      return;
    }
    if (!dataInicio) {
      this.alertService.show('Atenção', 'Informe a data de vencimento do 1º lançamento.', AlertType.warning);
      return;
    }

    const request = {
      valorTotal,
      intervalo,
      idFrequenciaRecorrencia,
      diaVencimentoFixo,
      quantidadeVezes,
      descricaoBase,
      dataInicio: new Date(dataInicio).toISOString(),
      idContaFinanceira: this.idContaFinanceiraControl?.value ?? null
    };

    this.isSimulando = true;
    this.contasPagarService.simularRecorrencia(request).subscribe({
      next: (res: any) => {
        this.isSimulando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Erro ao simular recorrência.', AlertType.error);
          return;
        }
        this.popularGridRecorrencia(res.parcelas || []);
      },
      error: () => {
        this.isSimulando = false;
        this.alertService.show('Erro', 'Erro ao comunicar-se com o servidor.', AlertType.error);
      }
    });
  }

  private popularGridParcelas(parcelas: any[]): void {
    this.parcelasArray.clear();
    parcelas.forEach((p: any) => {
      this.parcelasArray.push(this.fb.group({
        nroParcela: [p.nroParcela],
        dataVencimento: [p.dataVencimento ? ContasPagarUtil.parseDateLocal(p.dataVencimento) : null],
        valor: [p.valor],
        percentual: [p.percentual],
        descricao: [p.descricao]
      }));
    });
  }

  private popularGridRecorrencia(parcelas: any[]): void {
    this.parcelasArray.clear();
    parcelas.forEach((p: any) => {
      this.parcelasArray.push(this.fb.group({
        nroOcorrencia: [p.nroOcorrencia],
        dataCriacao: [p.dataCriacao ? ContasPagarUtil.parseDateLocal(p.dataCriacao) : null],
        dataVencimento: [p.dataVencimento ? ContasPagarUtil.parseDateLocal(p.dataVencimento) : null],
        descricao: [p.descricao],
        valor: [p.valor]
      }));
    });
  }

  public onParcelaValorChange(index: number): void {
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    this.atualizarPercentual(index, valorTotal);
    this.fecharSomaNaUltima(index, valorTotal);
  }

  /**
   * A soma das parcelas é sempre idêntica ao total do documento: a diferença de uma parcela editada vai para a
   * última (se a editada for a última, para a anterior), como a venda faz. Com uma parcela só, não há onde pôr.
   */
  private fecharSomaNaUltima(indexEditado: number, valorTotal: number): void {
    const total = UtilService.arredondarAbnt(valorTotal, 2);
    const n = this.parcelasArray.length;
    if (n < 2 || total <= 0) return;

    const alvo = indexEditado === n - 1 ? n - 2 : n - 1;
    const somaOutras = this.parcelasArray.controls.reduce((soma, ctrl, i) => soma + (i === alvo ? 0 : (ctrl.get('valor')?.value || 0)), 0);
    const valorAlvo = UtilService.arredondarAbnt(total - somaOutras, 2);
    if (valorAlvo < 0) return;

    this.parcelasArray.at(alvo).get('valor')?.setValue(valorAlvo, { emitEvent: false });
    this.atualizarPercentual(alvo, valorTotal);
  }

  private atualizarPercentual(index: number, valorTotal: number): void {
    const ctrl = this.parcelasArray.at(index);
    const valor = ctrl.get('valor')?.value || 0;
    const perc = valorTotal > 0 ? Math.round((valor / valorTotal) * 10000) / 100 : 0;
    ctrl.get('percentual')?.setValue(perc, { emitEvent: false });
  }

  public get somaParcelas(): number {
    if (this.isRecorrente) return 0;
    return this.parcelasArray.controls.reduce((soma, ctrl) => soma + (ctrl.get('valor')?.value || 0), 0);
  }

  public get somaParcelasValida(): boolean {
    if (this.isRecorrente || this.parcelasArray.length === 0) return true;
    const valorTotal = this.parentForm.get('dadosGerais.valorTotal')?.value || 0;
    return UtilService.arredondarAbnt(this.somaParcelas, 2) === UtilService.arredondarAbnt(valorTotal, 2);
  }


  onMonthSelected(date: Date, datepicker: MatDatepicker<Date>, control: AbstractControl | null): void {
    if (!this.isContaCartao || !control) return;
    const dia = this.diaVencimentoCartao;
    const adjusted = dia != null ? ContasPagarUtil.ajustarParaDiaVencimento(date, dia) : date;
    control.setValue(adjusted);
    datepicker.close();
  }

  onDateInput(event: Event, controlName: string, index?: number): void {
    const control = index !== undefined
      ? this.parcelasArray.at(index)?.get(controlName)
      : this.parcelamentoGroup.get(controlName);
    MaskService.onDateInput(event, control);
  }

}
